import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import {ProgressToolCall} from 'llm/chat-library/progress/ProgressTypes';
import {
  ParsedProgressToolCall,
  SearchNotebookEntriesToolCall,
  MCPToolCall,
  parseProgressToolCall,
} from 'llm/chat-library/tool-details/ParsedProgressToolCall';
import {ToolType, getToolIcon, isMCPTool} from 'llm/chat-library/tool-details/types';
import {useToolDisplayName} from 'llm/chat-library/tool-details/useToolDisplayName';
import LLMMarkdownSection from 'llm/markdown/LLMMarkdownSection';
import MonacoEditor from 'llm/monaco/MonacoEditor';
import {MonacoLanguage} from 'llm/monaco/MonacoTypes';
import {
  FONT_PROXIMA_12,
  FONT_PROXIMA_14,
  FONT_PROXIMA_18,
  FONT_WEIGHT_PROXIMA_REGULAR,
  FONT_WEIGHT_PROXIMA_SEMIBOLD,
} from 'styles/FontConstants';
import {GRAY_DARKER} from 'ui-library/Colors';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {assertNever} from 'util/Types';

// ============================================================================
// Lightweight rendering blocks
// ============================================================================

function MonoBlock({text}: {text: string}): JSX.Element {
  return <div className={css(styles.codeBlock)}>{text}</div>;
}

const MIN_EDITOR_HEIGHT = 120;
const MAX_EDITOR_HEIGHT = 320;
const BORDER_OFFSET = 2;

function ReadOnlyCodeBlock({code, language}: {code: string; language: MonacoLanguage}): JSX.Element {
  const [height, setHeight] = useState(MIN_EDITOR_HEIGHT);

  return (
    <div className={css(styles.editorContainer)}>
      <MonacoEditor
        value={code}
        language={language}
        height={height}
        width="100%"
        theme="vs"
        options={{readOnly: true, minimap: {enabled: false}, scrollBeyondLastLine: false, wordWrap: 'on'}}
        onChangeContentSize={(e) => {
          setHeight(
            Math.min(Math.max(MIN_EDITOR_HEIGHT, e.contentHeight + BORDER_OFFSET), MAX_EDITOR_HEIGHT)
          );
        }}
      />
    </div>
  );
}

function MarkdownBlock({markdown}: {markdown: string}): JSX.Element {
  return (
    <div className={css(styles.markdownContainer)}>
      <LLMMarkdownSection markdown={markdown} />
    </div>
  );
}

// ============================================================================
// Tool input/output formatting
// ============================================================================

function formatArgsForDisplay(args: Record<string, unknown>): string {
  return Object.entries(args)
    .map(([key, value]) => {
      if (typeof value === 'object' && value != null) {
        return `${key}: ${JSON.stringify(value, null, 2)}`;
      }
      return `${key}: ${value}`;
    })
    .join('\n');
}

function formatSearchNotebookEntriesInput(toolCall: SearchNotebookEntriesToolCall): string {
  const parts: Array<string> = [];

  const queries = Array.isArray(toolCall.queries) ? toolCall.queries : [];
  if (queries.length) {
    parts.push(`Queries: ${queries.map((q) => `"${q}"`).join(', ')}`);
  }

  const fields: Array<[string | undefined, string]> = [
    [toolCall.authoredBy?.join(', '), 'Authored By'],
    [toolCall.createdAfter, 'Created After'],
    [toolCall.createdBefore, 'Created Before'],
    [toolCall.modifiedAfter, 'Modified After'],
    [toolCall.modifiedBefore, 'Modified Before'],
    [toolCall.reviewCompletedAfter, 'Review Completed After'],
    [toolCall.reviewCompletedBefore, 'Review Completed Before'],
    [toolCall.reviewStatus, 'Review Status'],
    [toolCall.reviewRole, 'Review Role'],
    [toolCall.inProjectOrFolder, 'In Project/Folder'],
    [toolCall.inStudy, 'In Study'],
  ];

  for (const [value, label] of fields) {
    if (value) {
      parts.push(`${label}: ${value}`);
    }
  }

  if (toolCall.offset !== undefined && toolCall.offset > 0) {
    parts.push(`Offset: ${toolCall.offset}`);
  }

  return parts.length > 0 ? parts.join('\n') : 'No search parameters';
}

function isMCPToolCall(parsed: ParsedProgressToolCall): parsed is MCPToolCall {
  return isMCPTool(parsed.tool);
}

/**
 * Returns structured input rendering for a tool, or null if the tool has no
 * structured input (in which case the caller should render a fallback).
 *
 * Also used by ToolCallItem in ChatProgress to determine whether a tool
 * has structured detail worth showing in the activity modal.
 */
export function getToolInputBlock(
  toolCall: ProgressToolCall,
  parsed: ParsedProgressToolCall | null
): JSX.Element | null {
  if (parsed == null) {
    return null;
  }

  if (isMCPToolCall(parsed)) {
    if (!parsed.args || Object.keys(parsed.args).length === 0) {
      return null;
    }
    return <MonoBlock text={formatArgsForDisplay(parsed.args)} />;
  }

  switch (parsed.tool) {
    case ToolType.SearchTool:
      return (
        <MonoBlock
          text={`Query: "${parsed.query || ''}"${parsed.objectTypes?.length ? `\nObject Types: ${parsed.objectTypes.join(', ')}` : ''}`}
        />
      );
    case ToolType.SearchNotebookEntries:
      return <MonoBlock text={formatSearchNotebookEntriesInput(parsed)} />;
    case ToolType.SearchSequencesTool:
    case ToolType.QueryWarehouseTool:
      return <ReadOnlyCodeBlock code={parsed.query || ''} language="pgsql" />;
    case ToolType.GetWarehouseOverviewTool:
      return fallbackInputBlock(toolCall);
    case ToolType.ReadBenchlingDocumentTool:
      return <MonoBlock text={`Document ID: ${parsed.documentId || 'N/A'}`} />;
    case ToolType.AttachmentTool:
      return <MonoBlock text={`Attachment ID: ${parsed.attachmentId || 'N/A'}`} />;
    case ToolType.PublicDataSubAgent:
      return <MonoBlock text={`Query: "${parsed.query || ''}"`} />;
    case ToolType.IdsToModelsTool:
      return (
        <MonoBlock
          text={`Object IDs: ${parsed.objectIds?.join(', ') || 'N/A'}\nInclude Relevant Items: ${parsed.includeRelevantItems ?? false}`}
        />
      );
    case ToolType.PlottingTool:
      return <ReadOnlyCodeBlock code={parsed.code || 'No code provided'} language="python" />;
    case ToolType.GenerateImageTool:
      return <MonoBlock text={`Prompt: "${parsed.prompt || ''}"`} />;
    case ToolType.CustomCodeTool:
      return <ReadOnlyCodeBlock code={parsed.code || 'No code provided'} language="python" />;

    // TODO: Add structured input rendering for Think in the activity modal.
    case ToolType.Think:
    case ToolType.LoadSkill:
      return null;

    // N/A - these tools are not found in TOOLS_TO_DISPLAY.
    case ToolType.ResearchSubAgent:
    case ToolType.CompleteAnswer:
    case ToolType.CompleteAnswerTool:
    case ToolType.DeepResearchTool:
      return null;
    default:
      assertNever(parsed);
      return null;
  }
}

/**
 * Returns structured output rendering for a tool, or null if the tool has no
 * structured output (in which case the caller should render a fallback).
 */
export function getToolOutputBlock(
  toolCall: ProgressToolCall,
  parsed: ParsedProgressToolCall | null
): JSX.Element | null {
  if (parsed == null) {
    return null;
  }

  if (isMCPToolCall(parsed)) {
    return null;
  }

  switch (parsed.tool) {
    case ToolType.QueryWarehouseTool:
      return <MarkdownBlock markdown={toolCall.responseText?.trim() || 'No response available'} />;
    case ToolType.GetWarehouseOverviewTool:
      return fallbackOutputBlock(toolCall);

    // TODO: Add structured output rendering for these displayed tools.
    case ToolType.SearchTool:
    case ToolType.ReadBenchlingDocumentTool:
    case ToolType.SearchNotebookEntries:
    case ToolType.SearchSequencesTool:
    case ToolType.AttachmentTool:
    case ToolType.PublicDataSubAgent:
    case ToolType.IdsToModelsTool:
    case ToolType.PlottingTool:
    case ToolType.GenerateImageTool:
    case ToolType.CustomCodeTool:
    case ToolType.Think:
    case ToolType.LoadSkill:
      return null;

    // N/A - these tools are not found in TOOLS_TO_DISPLAY.
    case ToolType.ResearchSubAgent:
    case ToolType.CompleteAnswer:
    case ToolType.CompleteAnswerTool:
    case ToolType.DeepResearchTool:
      return null;
    default:
      assertNever(parsed);
      return null;
  }
}

function fallbackInputBlock(toolCall: ProgressToolCall): JSX.Element {
  const args = toolCall.args ?? {};
  const text = Object.keys(args).length > 0 ? formatArgsForDisplay(args) : 'No input parameters';
  return <MonoBlock text={text} />;
}

function fallbackOutputBlock(toolCall: ProgressToolCall): JSX.Element {
  return <MonoBlock text={toolCall.responseText?.trim() || 'No response available'} />;
}

// ============================================================================
// ToolDetail component
// ============================================================================

export interface ToolDetailProps {
  toolCall: ProgressToolCall;
}

/**
 * Displays detailed information about a specific tool call.
 * Parses the generic ProgressToolCall into typed fields at this boundary
 * so that input/output formatting can use well-typed properties.
 */
export function ToolDetail({toolCall}: ToolDetailProps): JSX.Element {
  const getToolDisplayNameFormatted = useToolDisplayName();
  const parsed = parseProgressToolCall(toolCall);

  return (
    <div className={css(styles.container)}>
      <div className={css(styles.header)}>
        <BenchlingIcon icon={getToolIcon(toolCall.tool)} className={css(styles.toolIcon)} />
        <div className={css(styles.toolName)}>
          {getToolDisplayNameFormatted(toolCall.tool, toolCall.args, toolCall.status)}
        </div>
      </div>

      <div className={css(styles.content)}>
        <div className={css(styles.section)}>
          <div className={css(styles.sectionLabel)}>Input:</div>
          {getToolInputBlock(toolCall, parsed) ?? fallbackInputBlock(toolCall)}
        </div>
        <div className={css(styles.section)}>
          <div className={css(styles.sectionLabel)}>Output:</div>
          {getToolOutputBlock(toolCall, parsed) ?? fallbackOutputBlock(toolCall)}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Styles
// ============================================================================

const styles = StyleSheet.create({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    padding: '4px',
  },

  header: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: '12px',
  },

  toolIcon: {
    fontSize: '18px',
    color: GRAY_DARKER,
  },

  toolName: {
    ...FONT_PROXIMA_18,
    fontWeight: FONT_WEIGHT_PROXIMA_REGULAR,
    color: GRAY_DARKER,
  },

  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },

  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },

  sectionLabel: {
    ...FONT_PROXIMA_14,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    color: GRAY_DARKER,
  },

  codeBlock: {
    ...FONT_PROXIMA_12,
    color: GRAY_DARKER,
    padding: '12px',
    backgroundColor: '#f8f9fa',
    border: '1px solid #e1e5e9',
    borderRadius: '4px',
    whiteSpace: 'pre-wrap',
    fontFamily: 'monospace',
    fontSize: '13px',
    lineHeight: '1.4',
    maxHeight: '250px',
    overflowY: 'auto',
  },

  editorContainer: {
    border: '1px solid #e1e5e9',
    borderRadius: '4px',
    overflow: 'hidden',
  },

  markdownContainer: {
    ...FONT_PROXIMA_14,
    padding: '0 12px',
    backgroundColor: '#fcfdfe',
    border: '1px solid #e1e5e9',
    borderRadius: '4px',
  },
});
